import { createContext, useState, useContext, useEffect } from 'react';

// Create context
const LocationContext = createContext();

/**
 * LocationProvider component to manage global location state
 * This includes:
 * - User's current location (from address search or geolocation)
 * - Selected restaurant location
 * - Persistence to localStorage
 * 
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Child components
 */
export function LocationProvider({ children }) {
  // Initialize state from localStorage if available
  const [userLocation, setUserLocation] = useState(() => {
    const saved = localStorage.getItem('userLocation');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const [selectedLocation, setSelectedLocation] = useState(() => {
    const saved = localStorage.getItem('selectedLocation');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Update localStorage when userLocation changes
  useEffect(() => {
    if (userLocation) {
      localStorage.setItem('userLocation', JSON.stringify(userLocation));
    } else {
      localStorage.removeItem('userLocation');
    }
  }, [userLocation]);

  // Update localStorage when selectedLocation changes
  useEffect(() => {
    if (selectedLocation) {
      localStorage.setItem('selectedLocation', JSON.stringify(selectedLocation));
    } else {
      localStorage.removeItem('selectedLocation');
    }
  }, [selectedLocation]);

  // Function to update user's location (from address or geolocation)
  const updateUserLocation = (location) => {
    setUserLocation(location);
  };

  // Function to select a restaurant location
  const selectLocation = (location) => {
    setSelectedLocation(location);
  };

  // Function to clear all location data
  const clearLocations = () => {
    setUserLocation(null);
    setSelectedLocation(null);
    localStorage.removeItem('userLocation');
    localStorage.removeItem('selectedLocation');
  };

  // Context value
  const value = {
    userLocation,
    selectedLocation,
    updateUserLocation,
    selectLocation,
    clearLocations,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

/**
 * Custom hook to use the location context
 * @returns {Object} Location context value
 */
export function useLocation() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}


